import { useEffect, useState, useCallback } from 'react';
import { getState } from './api';
import type { DashboardState } from './types';
import { StatusBadge } from './components/StatusBadge';
import { ControlButtons } from './components/ControlButtons';
import { ProcessPanel, QueuePanel, PrsPanel, PawnReadyPanel } from './components/Panels';
import { LogTail } from './components/LogTail';

// Auto-poll cadence. 60s instead of 5s because:
//   - Local data (queue, pid, circuit, worktrees) is fine at 60s for an
//     operator monitoring at human pace.
//   - GitHub data (PRs, pawn-ready issues) is backend-cached at 60s anyway,
//     so polling more often produces no fresher data — just wasted calls.
//   - The manual Refresh button (forces ?fresh=1 on the server) is the
//     escape hatch when you want an immediate up-to-date view.
const POLL_MS = 60_000;

export default function App() {
  const [state, setState] = useState<DashboardState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const refresh = useCallback(async (opts?: { fresh?: boolean }) => {
    if (opts?.fresh) setRefreshing(true);
    try {
      const s = await getState(opts);
      setState(s);
      setErr(null);
      setLastFetch(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (opts?.fresh) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => refresh(), POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Show the freshest GH cache age so the user knows how stale the PR/issue
  // panels are. Falls back to "—" when no GH data fetched yet.
  const ghAge = state?.ghCacheAgeS
    ? Math.min(...Object.values(state.ghCacheAgeS).filter((n) => Number.isFinite(n)), Infinity)
    : Infinity;
  const ghAgeLabel = Number.isFinite(ghAge) ? `${ghAge}s` : '—';

  return (
    <div className="app">
      <header className="topbar">
        <div className="title">
          <span className="logo">🤖</span>
          <h1>Skynet Dashboard</h1>
          <StatusBadge state={state} />
        </div>
        <div className="meta">
          {lastFetch > 0 && (
            <span title={`auto-poll every ${POLL_MS / 1000}s`}>
              last refresh: {new Date(lastFetch).toLocaleTimeString()} · gh cache age {ghAgeLabel}
            </span>
          )}
          <button
            onClick={() => refresh({ fresh: true })}
            disabled={refreshing}
            className="btn-refresh"
            title="Bypass the 60s GitHub cache and fetch a fresh snapshot now"
          >
            {refreshing ? '…' : '↻ Refresh'}
          </button>
          {err && <span className="errors"> · {err}</span>}
        </div>
      </header>

      <ControlButtons onAction={() => refresh({ fresh: true })} />

      {state ? (
        <div className="grid">
          <ProcessPanel state={state} />
          <QueuePanel state={state} />
          <PrsPanel state={state} />
          <PawnReadyPanel state={state} />
        </div>
      ) : (
        <p className="loading">loading state…</p>
      )}

      <LogTail />

      <footer className="muted">
        polls /api/state every {POLL_MS / 1000}s · backend caches gh calls 60s · /api/logs/tail every 10s · auth via Cloudflare Access (Google)
      </footer>
    </div>
  );
}
